import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { PresaveFansSummary } from 'fym-shared/src/smartlinks/presaveSubscriptions';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
import { FansGrowthChart } from './FansGrowthChart';
import { fansGrowthBuckets, formatBucketDay } from './fansGrowthSeries';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansGrowthPanelProps {
  summary: PresaveFansSummary;
}

/**
 * "Growth": the chart with its legend, and one line under it that reads the bar pair under the
 * pointer, or the sums of the period (design c1). The line is also the text twin of the chart.
 */
const FansGrowthPanelComponent: React.FC<FansGrowthPanelProps> = ({ summary }) => {
  const intl = useIntl();
  const [active, setActive] = useState<number | null>(null);

  const buckets = useMemo(() => fansGrowthBuckets(summary.series, summary.days, new Date()), [summary]);

  const readout = useMemo(() => {
    const bucket = active === null ? undefined : buckets[active];
    if (bucket !== undefined) {
      return intl.formatMessage(messages.growthDay, {
        date: formatBucketDay(intl, bucket.start),
        subscribed: intl.formatNumber(bucket.subscribed),
        unsubscribed: intl.formatNumber(bucket.unsubscribed),
      });
    }
    const sums = buckets.reduce((acc, item) => ({ up: acc.up + item.subscribed, down: acc.down + item.unsubscribed }), {
      up: 0,
      down: 0,
    });
    return intl.formatMessage(messages.growthPeriod, {
      days: summary.days,
      subscribed: intl.formatNumber(sums.up),
      unsubscribed: intl.formatNumber(sums.down),
    });
  }, [active, buckets, intl, summary.days]);

  return (
    <Panel>
      <PanelHead
        title={intl.formatMessage(messages.growthTitle)}
        right={
          <span className={styles.legend}>
            <span>
              <i className={styles.legendUp} />
              {intl.formatMessage(messages.growthSubscribed)}
            </span>
            <span>
              <i className={styles.legendDown} />
              {intl.formatMessage(messages.growthUnsubscribed)}
            </span>
          </span>
        }
      />
      <PanelBody>
        <FansGrowthChart buckets={buckets} active={active} onActive={setActive} />
        <p className={styles.readout} aria-live="polite">
          {readout}
        </p>
      </PanelBody>
    </Panel>
  );
};

export const FansGrowthPanel = TypedMemo(FansGrowthPanelComponent);
